import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './CustomDropdown.css';

const CustomDropdown = ({
    options = [],
    value = '',
    onChange,
    placeholder = "Select an option",
    disabled = false,
    searchable = false,
    className = '',
    error = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);
    const navigate = useNavigate();

    const selectedOption = options.find(opt => opt.value === value);
    const displayText = selectedOption ? selectedOption.label : placeholder;

    // Filter options based on search
    const filteredOptions = searchable
        ? options.filter(opt =>
            opt.label.toLowerCase().includes(searchTerm.toLowerCase()))
        : options;

    // Handle option selection
    const handleSelect = (option) => {
        onChange(option.value);
            window.scrollTo({
        top: 0,
        behavior: 'smooth',
    });
        setIsOpen(false);
        setSearchTerm('');
    };

    // Toggle dropdown
    const toggleDropdown = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
            if (searchable && !isOpen) {
                // Focus search input when opening
                setTimeout(() => {
                    const searchInput = dropdownRef.current?.querySelector('.search-input');
                    if (searchInput) searchInput.focus();
                }, 0);
            }
        }
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Handle keyboard navigation
    const handleKeyDown = (e) => {
        if (!isOpen) return;

        const currentIndex = selectedOption
            ? filteredOptions.findIndex(opt => opt.value === selectedOption.value)
            : -1;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                const nextIndex = currentIndex < filteredOptions.length - 1 ? currentIndex + 1 : 0;
                if (filteredOptions[nextIndex]) {
                    onChange(filteredOptions[nextIndex].value);
                }
                break;

            case 'ArrowUp':
                e.preventDefault();
                const prevIndex = currentIndex > 0 ? currentIndex - 1 : filteredOptions.length - 1;
                if (filteredOptions[prevIndex]) {
                    onChange(filteredOptions[prevIndex].value);
                }
                break;

            case 'Enter':
                e.preventDefault();
                if (selectedOption) {
                    setIsOpen(false);
                    setSearchTerm('');
                }
                break;

            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                setSearchTerm('');
                break;

            case 'Tab':
                setIsOpen(false);
                setSearchTerm('');
                break;
        }
    };

    // Clear search when closing dropdown
    useEffect(() => {
        if (!isOpen) {
            setSearchTerm('');
        }
    }, [isOpen]);

    return (
        <div className={`custom-dropdown-wrapper ${className} ${error ? 'error' : ''}`}>
            <div
                className={`custom-dropdown`}
                ref={dropdownRef}
                onKeyDown={handleKeyDown}
            >
                <button
                    type="button"
                    className={`dropdown-btn ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
                    onClick={toggleDropdown}
                    disabled={disabled}
                    tabIndex={0}
                >
                    <span>{displayText}</span>
                    <span className="dropdown-arrow">▼</span>
                </button>

                {isOpen && (
                    <div className="dropdown-options show">

                        {/* Options list */}
                        {filteredOptions.length > 0 ? (
                            <div className="options-list">
                                {filteredOptions.map((option) => (
                                    <div
                                        key={option.value}
                                        className={`option-item ${value === option.value ? 'selected' : ''}`}
                                        onClick={() => handleSelect(option)}
                                        tabIndex={0}
                                        onKeyDown={(e) => e.key === 'Enter' && handleSelect(option)}
                                    >
                                        {option.label}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="option-item no-results">
                                {searchable && searchTerm ? "No results found" : "No options available"}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CustomDropdown;